/**
 * Groq Data Source - v13 Section 6.10
 *
 * Direct fetcher for Groq's model list.
 * Groq provides fast inference for open models (Llama, Mixtral, etc.)
 *
 * ╔══════════════════════════════════════════════════════════════════════════════╗
 * ║  NO HARDCODED MODEL DATA - All model info comes from Groq API                ║
 * ║                                                                              ║
 * ║  Groq API provides: model ID, context_window, active status                  ║
 * ║  Groq API does NOT provide: pricing, max completion tokens                   ║
 * ║  These should be fetched from llm-prices.com or user-configured              ║
 * ╚══════════════════════════════════════════════════════════════════════════════╝
 *
 * @see https://console.groq.com/docs/models
 */
/**
 * Create display name from model ID using generic cleanup
 * No hardcoded mapping - just transform the ID into readable format
 */
const createDisplayName = (modelId) => {
    return modelId
        .replace(/-/g, ' ')
        .replace(/(\d+)b/gi, '$1B')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
        + ' (Groq)';
};
/**
 * Filter for text/chat models (exclude audio, whisper, etc.)
 * This is TYPE-based filtering, not model-name filtering
 */
const isChatModel = (modelId) => {
    const id = modelId.toLowerCase();
    // Exclude non-chat model TYPES (embeddings, tts, transcription)
    if (id.includes('whisper'))
        return false;
    if (id.includes('distil-whisper'))
        return false;
    if (id.includes('embed'))
        return false;
    if (id.includes('tts'))
        return false;
    return true;
};
/**
 * Fetch models directly from Groq API
 * Returns only API-provided data - no hardcoded pricing or limits
 *
 * @param apiKey - Groq API key
 * @returns Array of ModelMetadata for Groq models (with null for missing fields)
 */
export const fetchFromGroq = async (apiKey) => {
    const response = await fetch('https://api.groq.com/openai/v1/models', {
        headers: {
            Authorization: `Bearer ${apiKey}`,
            'Content-Type': 'application/json',
        },
    });
    if (!response.ok) {
        throw new Error(`Groq API returned ${response.status}: ${response.statusText}`);
    }
    const data = await response.json();
    if (!data.data || !Array.isArray(data.data)) {
        throw new Error('Invalid response format from Groq API');
    }
    const models = [];
    for (const model of data.data) {
        // Skip inactive models
        if (model.active === false)
            continue;
        // Skip non-chat models (type-based filtering)
        if (!isChatModel(model.id))
            continue;
        // Use only API-provided data - null for missing fields
        models.push({
            id: model.id,
            provider: 'groq',
            displayName: createDisplayName(model.id),
            contextWindow: model.context_window ?? null, // API provides this
            maxCompletionTokens: null, // API doesn't provide this
            pricing: null, // API doesn't provide pricing - get from llm-prices.com
            capabilities: [], // API doesn't provide this
            zeroDataRetention: true, // Groq is known to be ZDR - structural knowledge
            isReasoningModel: null, // API doesn't provide this
        });
    }
    return models;
};
